/**
 * Navigation command source for the universal command registry (#1514).
 *
 * Flattens the two single-source nav definitions — ``adminNavGroups``
 * (adminNav.ts) and ``navGroups`` (crmNav.ts) — into searchable "go to" entries.
 * Every standalone routed destination worth jumping to (Connect your AI,
 * Personal settings, ...) already lives in a nav group, so the flatten covers
 * them; this module adds no hardcoded duplicates.
 *
 * RBAC gating REUSES the SAME predicates the sidebars use:
 *  - CRM items: ``isItemVisible`` (crmNav.ts) against the module/flag/back-office ctx.
 *  - Admin items: visible ONLY when ``role === "admin"`` (the admin SHELL rule —
 *    /admin/* pages are never reachable by a plain agent or a back-office persona)
 *    AND the item's optional ``module`` gate passes. adminNav items carry only
 *    ``module`` gates (no per-flag), matching AdminSidebar.
 *
 * Pure + dependency-free (no React) so it is unit-testable in isolation.
 */
import {
  adminNavGroups,
  type NavGroup as AdminNavGroup,
} from "../../components/layout/adminNav";
import {
  navGroups,
  isItemVisible,
  type NavGroup as CrmNavGroup,
} from "../../components/layout/crmNav";
import type { CommandContext, NavCommand } from "./types";

const SEP = "›";

function adminGroupCommands(
  group: AdminNavGroup,
  ctx: CommandContext,
): NavCommand[] {
  // Admin SHELL rule: /admin/* pages are admin-role-only. A plain agent / any
  // back-office persona never gets an admin entry.
  if (ctx.role !== "admin") return [];
  return group.items
    .filter((item) => !item.module || ctx.isEnabled(item.module))
    .filter((item) => !item.superuserOnly || ctx.isSuperuser)
    .map((item) => ({
      id: `nav:${item.to}`,
      kind: "nav" as const,
      label: item.label,
      sublabel: `Admin ${SEP} ${group.label} ${SEP} ${item.label}`,
      icon: item.icon,
      url: item.to,
    }));
}

function crmGroupCommands(group: CrmNavGroup, ctx: CommandContext): NavCommand[] {
  return group.items
    .filter((item) =>
      isItemVisible(item, {
        isEnabled: ctx.isEnabled,
        has: ctx.has,
        isBackOffice: ctx.isBackOffice,
        farmWideCanvass: ctx.farmWideCanvass,
        hasOwnBook: ctx.hasOwnBook,
        paralegalDesk: ctx.paralegalDesk,
        financeDesk: ctx.financeDesk,
      }),
    )
    .map((item) => ({
      id: `nav:${item.to}`,
      kind: "nav" as const,
      label: item.label,
      sublabel: `CRM ${SEP} ${group.label} ${SEP} ${item.label}`,
      icon: item.icon,
      url: item.to,
    }));
}

/**
 * Build the filtered, RBAC-gated navigation command list for the given context.
 * Deterministic: admin groups first (when admin), then CRM groups, each in
 * their declared order. De-duped by url (e.g. /crm/leaderboard appears in both
 * the Oversight and My Stats groups — only one is ever visible to a given ctx,
 * but the de-dup keeps the registry stable if both ever pass).
 */
export function buildNavCommands(ctx: CommandContext): NavCommand[] {
  const out: NavCommand[] = [];
  for (const group of adminNavGroups) out.push(...adminGroupCommands(group, ctx));
  for (const group of navGroups) out.push(...crmGroupCommands(group, ctx));
  const seen = new Set<string>();
  return out.filter((c) => {
    if (seen.has(c.url)) return false;
    seen.add(c.url);
    return true;
  });
}
